import { Injectable, DestroyRef, inject, effect, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, debounceTime } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { SdSnackBarStore } from '../../shared/sd-snack-bar/store/sd-snack-bar.store';
import { DictionaryGlobalStore } from '../../dictionary/store/dictionary-global.store';
import { tokenSelector } from '../../auth/store/auth.selectors';
import { LogOut } from '../../auth/store/auth.actions';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { LocationService } from './location.service';

@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly snackBarStore = inject(SdSnackBarStore);
  private readonly dictStore = inject(DictionaryGlobalStore);
  private readonly locationService = inject(LocationService);
  private readonly destroyRef = inject(DestroyRef);

  private static readonly FALLBACK_MESSAGE =
    'A new version of the application is available. Click update, or you can close this message to update on your next login.';
  private static readonly FALLBACK_UPDATE = 'Update';
  private static readonly FALLBACK_CLOSE = 'Close';

  private readonly dictionary = this.dictStore.dictionary;
  private readonly pendingUpdate = signal(false);
  private prompted = false;

  constructor() {
    this.setupUpdateChecks();

    effect(() => {
      const dict = this.dictStore.dictionary();
      const pending = this.pendingUpdate();
      if (dict && pending) {
        this.pendingUpdate.set(false);
        this.promptUserToUpdate();
      }
    });
  }

  private setupUpdateChecks(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    try {
      this.swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          this.scheduleUpdatePrompt();
        });

      this.swUpdate.checkForUpdate();

      fromEvent(document, 'visibilitychange')
        .pipe(
          filter(() => !document.hidden),
          debounceTime(1000),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          this.checkTokenExpiration();
          this.swUpdate.checkForUpdate();
        });
    } catch (error) {
      console.error('Error setting up service worker updates:', error);
    }
  }

  private scheduleUpdatePrompt(): void {
    if (this.prompted || this.pendingUpdate()) {
      return;
    }

    if (this.dictionary()) {
      this.promptUserToUpdate();
      return;
    }

    this.pendingUpdate.set(true);
  }

  private promptUserToUpdate(): void {
    this.prompted = true;
    this.snackBarStore.show({
      message:
        this.dictionary()?.updateversionsnackbar_message ??
        UpdateService.FALLBACK_MESSAGE,
      duration: 0,
      verticalPosition: 'bottom',
      panelClass: ['custom-snackbar', 'orange-snackbar', 'update-snackbar'],
      actions: [
        {
          text:
            this.dictionary()?.globalparams_update ??
            UpdateService.FALLBACK_UPDATE,
          primary: false,
          callback: () => {
            this.swUpdate.activateUpdate().then(() => {
              this.locationService.reload();
            });
          },
        },
        {
          text:
            this.dictionary()?.globalparams_close ??
            UpdateService.FALLBACK_CLOSE,
          primary: false,
          closeOnly: true,
          callback: () => undefined,
        },
      ],
    });
  }

  private checkTokenExpiration(): void {
    const token = this.store.selectSignal(tokenSelector)();

    if (!token) {
      return;
    }

    if (this.isTokenExpired(token)) {
      this.store.dispatch(new LogOut());
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      if (!token) {
        return true;
      }

      const decoded: JwtPayload = jwtDecode<JwtPayload>(token);

      if (!decoded.exp) {
        return true;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const isExpired = decoded.exp <= currentTime;

      return isExpired;
    } catch {
      return true;
    }
  }
}
